<template>
  <div class="institute">
    <div class="container my-5 text-center">
      <h2 class="page-title text-brand">{{ $t("institute.introTitle") }}</h2>
      <p class="page-subtitle text-muted mt-2">
        {{ $t("institute.intro") }}
      </p>
    </div>

    <div class="container">
      <!-- 篩選列：改為置中 -->
      <div class="row g-2 align-items-end mb-4 justify-content-center text-center filter-row">
        <!-- 縣市 -->
        <div class="col-auto">
          <div class="select-wrap">
            <select class="form-select rounded-input brand control-input" v-model="selectedCity" @change="onCityChange">
              <option value="">{{ $t("institute.allcity") || "請選擇縣市" }}</option>
              <option v-for="c in cities" :key="c" :value="c">{{ c }}</option>
            </select>
            <span class="material-symbols-outlined select-caret" aria-hidden="true">arrow_drop_down</span>
          </div>
        </div>

        <!-- 區 -->
        <div class="col-auto">
          <div class="select-wrap">
            <select class="form-select rounded-input brand control-input" v-model="selectedDistrict" :disabled="!selectedCity">
              <option value="">{{ $t("institute.alldistrict") || "請選擇區域" }}</option>
              <option v-for="d in (cityToDistricts[selectedCity] || [])" :key="d" :value="d">
                {{ d }}
              </option>
            </select>
            <span class="material-symbols-outlined select-caret" aria-hidden="true">arrow_drop_down</span>
          </div>
        </div>

        <!-- 關鍵字 -->
        <div class="col-auto">
          <input
            type="text"
            class="form-control rounded-input brand control-input control-input--wide"
            v-model="searchInput"
            :placeholder="$t('institute.searchPlaceholder') || '輸入名稱 / 關鍵字'"
            @keyup.enter="applySearch"
          />
        </div>

        <!-- 搜尋按鈕（藍底常駐） -->
        <div class="col-auto">
          <button class="btn btn-brand btn-icon" @click="applySearch" aria-label="Search">
            <span class="material-symbols-outlined align-middle">search</span>
          </button>
        </div>
      </div>

      <hr class="brand-divider" />

      <!-- 地圖（新增） -->
      <div v-if="showMap" class="map-wrap">
        <div v-if="mapsError" class="text-danger small mb-2">{{ mapsError }}</div>
        <div ref="gmap" class="gmap"></div>
      </div>


      <!-- 清單外加窄版容器 -->
      <div class="list-wrap">
        <table class="table table-borderless text-start institute-table">
          <tbody>
            <tr v-for="(item, index) in filteredList" :key="index">
              <!-- Link 圖示：有網址才顯示 -->
              <td class="link-col">
                <a
                  v-if="item.url"
                  :href="normalizeUrl(item.url)"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="link-icon gray"
                  aria-label="前往官網"
                  title="前往官網"
                >
                  <span class="material-symbols-outlined">link</span>
                </a>
              </td>

              <!-- 名稱：外觀一律灰色、無底線；若有網址仍可點 -->
              <th scope="row" class="fw-normal name-col">
                <a
                  v-if="item.url"
                  :href="normalizeUrl(item.url)"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="name-plain"
                >
                  {{ item.name }}
                </a>
                <span v-else class="name-plain">{{ item.name }}</span>
              </th>

              <!-- 地址：灰色 -->
              <td class="addr-col">{{ item.address }}</td>
            </tr>

            <tr v-if="filteredList.length === 0">
              <td colspan="3" class="text-muted">
                {{ $t("institute.noResult") || "查無結果，試試調整條件或關鍵字。" }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import districtsData from "@/lib/taiwan_districts.json";

export default {
  name: "Institute",
  data() {
    return {
      list: [],

      selectedCity: "",
      selectedDistrict: "",
      searchInput: "",
      searchKeyword: "",
      cities: [],
      cityToDistricts: {},
      zipToCityDistrict: {},
      isLoading: false,
      loadError: null,

      // === 地圖新增 ===
      showMap: false, // 是否顯示地圖

      map: null,
      infoWindow: null,
      markers: [],
      mapsReady: false,
      mapsError: null,
    };
  },

  created() {
    this.initDistrictMeta();
    this.loadInstituteList();
  },

  async mounted() {
    if (!this.showMap) return;

    await this.loadGoogleMapsScript();
    if (this.mapsReady) {
      this.initMap();
      this.renderMarkers(this.filteredList);
    }
  },


  computed: {
    filteredList() {
      const geoFiltered = this.list.filter(item => {
        const parsed = this.parseByZipOrAddress(item.address || "");
        if (this.selectedCity && this.normalizeCity(parsed.city) !== this.normalizeCity(this.selectedCity)) return false;
        if (this.selectedDistrict && parsed.district !== this.selectedDistrict) return false;
        return true;
      });

      const kw = (this.searchKeyword || "").trim().toLowerCase();
      let result = geoFiltered;
      if (kw) {
        result = geoFiltered.filter(i => {
          const hay = `${i.name || ""} ${i.address || ""} ${i.phone || ""}`.toLowerCase();
          return hay.includes(kw);
        });
      }

      return result.slice().sort((a, b) => this.extractZip(a.address) - this.extractZip(b.address));
    },
  },

  watch: {
    // 篩選同步更新地圖 markers
    filteredList: {
      handler(list) {
        if (!this.mapsReady || !this.map) return;
        this.renderMarkers(list);
      },
      deep: true,
    },
    showMap: {
      async handler(val) {
        if (!val) return;

        // 地圖 DOM 要先出現
        await this.$nextTick();

        // 已經初始化過就不用重跑
        if (this.map) {
          // 顯示回來時，Google Map 有時會需要 trigger resize 才會正常
          window.google?.maps?.event?.trigger(this.map, "resize");
          this.renderMarkers(this.filteredList);
          return;
        }

        await this.loadGoogleMapsScript();
        if (this.mapsReady) {
          this.initMap();
          this.renderMarkers(this.filteredList);
        }
      },
    },

  },

  methods: {
    // === 地圖方法（新增） ===
    async loadGoogleMapsScript() {
      this.mapsError = null;

      if (window.google?.maps) {
        this.mapsReady = true;
        return;
      }

      const apiKey = process.env.VUE_APP_GOOGLE_MAPS_API_KEY;
      if (!apiKey) {
        this.mapsError = "缺少 VUE_APP_GOOGLE_MAPS_API_KEY（請確認 .env 並重啟 npm run serve）";
        this.mapsReady = false;
        return;
      }

      const src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}`;

      try {
        await new Promise((resolve, reject) => {
          const s = document.createElement("script");
          s.src = src;
          s.async = true;
          s.defer = true;
          s.onload = resolve;
          s.onerror = reject;
          document.head.appendChild(s);
        });
        this.mapsReady = true;
      } catch (e) {
        this.mapsError = "Google Maps 載入失敗：請檢查 API key 是否啟用 Maps JavaScript API，以及網域限制是否包含 localhost/正式網域。";
        this.mapsReady = false;
      }
    },

    initMap() {
      const center = { lat: 23.7, lng: 121.0 };

      this.map = new window.google.maps.Map(this.$refs.gmap, {
        center,
        zoom: 7,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
      });

      this.infoWindow = new window.google.maps.InfoWindow();
    },

    clearMarkers() {
      this.markers.forEach(m => m.setMap(null));
      this.markers = [];
    },

    renderMarkers(list) {
      this.clearMarkers();

      if (!list || list.length === 0) {
        this.map.setCenter({ lat: 23.7, lng: 121.0 });
        this.map.setZoom(7);
        return;
      }

      const bounds = new window.google.maps.LatLngBounds();

      list.forEach(item => {
        if (typeof item.lat !== "number" || typeof item.lng !== "number") return;

        const pos = { lat: item.lat, lng: item.lng };

        const marker = new window.google.maps.Marker({
          position: pos,
          map: this.map,
          title: item.name || "",
        });

        marker.addListener("click", () => {
          const url = item.url ? this.normalizeUrl(item.url) : "";
          const addr = item.address ? `<div>${item.address}</div>` : "";
          const phone = item.phone ? `<div>☎ ${item.phone}</div>` : "";
          const link = url
            ? `<div style="margin-top:6px;"><a href="${url}" target="_blank" rel="noopener noreferrer">前往官網</a></div>`
            : "";

          this.infoWindow.setContent(`
            <div style="font-size:14px;line-height:1.4;">
              <div style="font-weight:600;margin-bottom:4px;">${item.name || ""}</div>
              ${addr}
              ${phone}
              ${link}
            </div>
          `);
          this.infoWindow.open(this.map, marker);
        });

        this.markers.push(marker);
        bounds.extend(pos);
      });

      if (!bounds.isEmpty()) {
        this.map.fitBounds(bounds);
        const z = this.map.getZoom();
        if (z > 16) this.map.setZoom(16);
      }
    },

    // === 你原本 methods（保留） ===
    initDistrictMeta() {
      const cities = [];
      const cityToDistricts = {};
      const zipToCityDistrict = {};

      (districtsData || []).forEach(cityObj => {
        const city = cityObj?.name || "";
        if (!city) return;
        cities.push(city);

        const dists = (cityObj.districts || []).map(d => d.name).filter(Boolean);
        cityToDistricts[city] = dists;

        (cityObj.districts || []).forEach(d => {
          if (d?.zip) {
            zipToCityDistrict[String(d.zip)] = { city, district: d.name };
          }
        });
      });

      this.cities = cities;
      this.cityToDistricts = cityToDistricts;
      this.zipToCityDistrict = zipToCityDistrict;
    },

    // 改抓含 lat/lng 的檔案（記得把檔案放到 public/data）
    async loadInstituteList() {
      this.isLoading = true;
      this.loadError = null;

      try {
        const baseUrl =
          "https://pub-d167b6564ae04575a23d6d8c224f89e2.r2.dev/institute.with_latlng.json";

        // 加 querystring，避免 R2 / CDN 快取舊資料
        const url = `${baseUrl}?v=${Date.now()}`;

        const res = await fetch(url, { cache: "no-store" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const json = await res.json();
        this.list = Array.isArray(json) ? json : (json.institute || []);
      } catch (err) {
        console.error("載入合作清單失敗：", err);
        this.loadError = "合作清單載入失敗";
      } finally {
        this.isLoading = false;
      }
    },

    onCityChange() {
      this.selectedDistrict = "";
    },
    applySearch() {
      this.searchKeyword = this.searchInput;
    },
    normalizeCity(city) {
      return String(city || "").replace(/台/g, "臺");
    },
    parseByZipOrAddress(addr) {
      const s = String(addr || "").trim();
      const zipMatch = s.match(/^(\d{3,5})/);
      if (zipMatch) {
        const hit = this.zipToCityDistrict[zipMatch[1]];
        if (hit) return { city: hit.city, district: hit.district };
      }
      const noZip = s.replace(/^\d{3,5}/, "");
      const mCity = noZip.match(/^(.*?[市縣])/);
      const city = mCity ? mCity[1] : "";
      const rest = mCity ? noZip.slice(mCity[1].length) : noZip;
      const mDist = rest.match(/(.*?[區鎮市鄉])/);
      const dist = mDist ? mDist[1] : "";
      return { city: this.normalizeCity(city), district: dist };
    },
    extractZip(addr) {
      const match = String(addr || "").trim().match(/^(\d{3,5})/);
      return match ? parseInt(match[1], 10) : 99999;
    },
    normalizeUrl(url) {
      if (!url) return "";
      const first = String(url).split(/[;；]/)[0].trim();
      if (!first) return "";
      if (/^https?:\/\//i.test(first)) return first;
      return "https://" + first.replace(/^\/+/, "");
    },
  },
};
</script>

<style scoped>
/* —— 標題與分隔線 —— */
.page-title.text-brand { color: #5fa4b8; }
.brand-divider { border-top: 1px solid #0071bb; opacity: 1; }

/* —— 篩選列置中 —— */
.filter-row { row-gap: 12px; }

/* 控制各欄位寬度，搭配 col-auto 置中排 */
.control-input {
  width: 240px;
  max-width: 80vw;
}
.control-input--wide { width: 320px; }

/* —— 表單控制：圓角 + 藍邊藍字 + 自訂下拉箭頭 —— */
.rounded-input {
  border-radius: 12px !important;
  border: 1px solid #0071bb !important;
  color: #5fa4b8;
  background: #fff;
  height: 50px;
  padding: 0.5rem 2.5rem 0.5rem 0.9rem;
  appearance: none; -webkit-appearance: none; -moz-appearance: none;
}
.rounded-input:focus {
  box-shadow: 0 0 0 0.2rem rgba(0,113,187,.15);
  outline: none;
  color: #5fa4b8;
}

.select-wrap { position: relative; }
.select-caret {
  position: absolute;
  right: 10px; top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  font-size: 22px;
  color: #5fa4b8;
}

/* —— 搜尋按鈕：藍底常駐 —— */
.institute .btn.btn-brand,
.btn.btn-brand {
  background-color: #5fa4b8 !important;
  border-color: #5fa4b8 !important;
  color: #fff !important;
}
.institute .btn.btn-brand:hover,
.institute .btn.btn-brand:focus,
.institute .btn.btn-brand:active,
.btn.btn-brand:hover,
.btn.btn-brand:focus,
.btn.btn-brand:active {
  background-color: #5fa4b8 !important;
  border-color: #5fa4b8 !important;
  color: #fff !important;
  box-shadow: none;
}

.btn-icon {
  display: inline-flex; align-items: center; justify-content: center;
  min-height: 50px; width: 70px;
  border-radius: 12px;
  padding: 0;
}
.btn-icon .material-symbols-outlined { font-size: 26px; line-height: 1; color: #fff; }

/* === 地圖樣式（新增） === */
.map-wrap {
  max-width: 960px;
  margin: 0 auto 24px;
  padding: 0 48px;
}
.gmap {
  width: 100%;
  height: 420px;
  border-radius: 16px;
  border: 1px solid rgba(0, 113, 187, .25);
  overflow: hidden;
}

/* —— 清單窄版容器（左右留白） —— */
.list-wrap {
  max-width: 960px;
  margin: 0 auto;
  padding: 0 48px;
}

/* —— 清單整體灰字 —— */
.institute-table { color: #707070; font-weight: 400; }

.link-col { width: 40px; vertical-align: middle; }
.link-icon {
  display: inline-flex; align-items: center;
  color: #707070; text-decoration: none;
}
.link-icon .material-symbols-outlined { font-size: 22px; }

.name-col { width: 35%; }
.name-plain {
  color: #707070; text-decoration: none; cursor: pointer;
}
.name-plain:hover, .name-plain:focus {
  color: #707070; text-decoration: none;
}

.addr-col { color: #707070; }

@media (max-width: 768px) {
  .control-input { width: 220px; }
  .control-input--wide { width: 260px; }
  .addr-col { word-break: break-all; }

  .map-wrap { padding: 0 16px; }
  .gmap { height: 360px; }
  .list-wrap { padding: 0 16px; }
}
</style>
